import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../homepage/navbar/Navbar';
import { useThemeContext } from '../../context/theme-context';
import { FiArrowLeft, FiEdit, FiTrash2, FiEye, FiEyeOff } from 'react-icons/fi';
import './admin.css';

const AdminBlogs = () => {
  const { themeState } = useThemeContext();
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setError(null);
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/blogs?includeUnpublished=true`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        },
        credentials: 'include'
      });

      if (res.status === 401 || res.status === 403) {
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }

      const json = await res.json();
      if (res.ok) {
        setBlogs(json.data || []);
      } else {
        setError(json.message || 'Failed to load blogs');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublished = async (id, currentPublished) => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      
      const resGet = await fetch(`${API_URL}/api/blogs/${id}`);
      const jsonGet = await resGet.json();
      if (!resGet.ok || !jsonGet.data) {
        throw new Error(jsonGet.message || 'Failed to fetch details before toggling');
      }
      
      const updatedBlog = { ...jsonGet.data, published: !currentPublished };
      
      const res = await fetch(`${API_URL}/api/blogs/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(updatedBlog),
        credentials: 'include'
      });
      
      const json = await res.json();
      if (res.ok) {
        setBlogs(blogs.map(b => b._id === id ? { ...b, published: !currentPublished } : b));
      } else {
        setError(json.message || 'Failed to update publication status');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteBlog = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) {
      return;
    }

    try {
      setError(null);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/blogs/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        },
        credentials: 'include'
      });

      if (res.status === 401 || res.status === 403) {
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }

      const json = await res.json();
      if (res.ok) {
        setBlogs(blogs.filter(blog => blog._id !== id));
      } else {
        setError(json.message || 'Failed to delete blog');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <main className={`${themeState.primary} ${themeState.background} admin__page`}>
      <Navbar />

      <section className="admin__container">
        <br/>
        <div className="admin__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button className="btn sm" onClick={() => navigate('/admin')}>← Back</button>
            <h2>Blogs Management</h2>
          </div>
          <button className="btn primary sm" onClick={() => navigate('/admin/blogs/new')}>+ Add Blog</button>
        </div>
        <p className="muted">Manage, edit, publish, or delete blog articles.</p>

        {error && <div className="admin__error">{error}</div>}
        {loading && <div className="admin__loading">Loading blogs...</div>}

        {!loading && (
          <div className="admin__table-wrap" style={{ marginTop: '1.5rem' }}>
            <table className="admin__table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {blogs.length === 0 && (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '2rem' }}>
                      No blog posts found. Click "+ Add Blog" to create your first article.
                    </td>
                  </tr>
                )}
                {blogs.map(blog => (
                  <tr key={blog._id}>
                    <td className="admin__message" style={{ fontWeight: 600 }}>{blog.title}</td>
                    <td>{blog.category}</td>
                    <td>
                      <span style={{
                        padding: '0.2rem 0.6rem',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        background: blog.published ? 'rgba(76, 175, 80, 0.15)' : 'rgba(158, 158, 158, 0.2)',
                        color: blog.published ? '#2e7d32' : '#616161'
                      }}>
                        {blog.published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td>{new Date(blog.createdAt).toLocaleDateString()}</td>
                    <td style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                      <button 
                        className="btn sm" 
                        onClick={() => handleTogglePublished(blog._id, blog.published)}
                        style={{ 
                          background: blog.published ? 'rgba(158, 158, 158, 0.15)' : 'rgba(76, 175, 80, 0.15)', 
                          color: blog.published ? '#616161' : '#2e7d32',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.3rem'
                        }}
                        title={blog.published ? 'Hide from public site' : 'Show on public site'}
                      >
                        {blog.published ? <FiEyeOff /> : <FiEye />}
                        {blog.published ? 'Hide' : 'Show'}
                      </button>
                      <button 
                        className="btn sm" 
                        onClick={() => navigate(`/admin/blogs/edit/${blog._id}`)}
                        style={{ 
                          background: 'rgba(8, 163, 194, 0.15)', 
                          color: 'var(--color-primary)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.3rem'
                        }}
                      >
                        <FiEdit /> Edit
                      </button>
                      <button 
                        className="btn sm" 
                        onClick={() => handleDeleteBlog(blog._id, blog.title)}
                        style={{ 
                          background: 'rgba(176, 0, 32, 0.15)', 
                          color: '#b00020',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.3rem'
                        }}
                      >
                        <FiTrash2 /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
};

export default AdminBlogs;
